import * as amplitude from "@amplitude/analytics-browser";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { ErrorBoundary, reactPlugin } from "@datadog/browser-rum-react";
import { createBrowserRouter } from "@datadog/browser-rum-react/react-router-v6";
import { ThemeProvider } from "@mui/material";
import { composeWrappers, lazyComponent } from "@redotech/react-util/component";
import { HidProvider } from "@redotech/react-util/hid";
import { RedoClient } from "@redotech/redo-api-client";
import { initI18n } from "@redotech/redo-i18n/i18n";
import { ActionPanelComponentContext } from "@redotech/redo-merchant-app-common/action-panel";
import { AlertContextProvider } from "@redotech/redo-merchant-app-common/alert";
import {
  AuthContext,
  AuthProvider,
} from "@redotech/redo-merchant-app-common/auth";
import { RedoMerchantClientProvider } from "@redotech/redo-merchant-app-common/client/context";
import { RedoConciergeRpcClientProvider } from "@redotech/redo-merchant-app-common/concierge-rpc-client-provider";
import {
  DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN,
  REDO_API_URL,
  REDO_MERCHANT_APP_VERSION,
  REDO_MERCHANT_SERVER_URL,
} from "@redotech/redo-merchant-app-common/config";
import { CurrencyExchangeProvider } from "@redotech/redo-merchant-app-common/currency-exchange";
import { RedoCustomerRpcClientProvider } from "@redotech/redo-merchant-app-common/customer-rpc-client-provider";
import { MerchantAppEventServerProvider } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server-provider";
import { GlobalAgentProvider } from "@redotech/redo-merchant-app-common/global-agent-context";
import { RedoIntegrationsRpcClientProvider } from "@redotech/redo-merchant-app-common/integrations-rpc-client-provider";
import { RedoInventoryRpcClientProvider } from "@redotech/redo-merchant-app-common/inventory-rpc-client-provider";
import { RedoLiquidationRpcClientProvider } from "@redotech/redo-merchant-app-common/liquidation-rpc-client-provider";
import { RedoMarketingRpcClientProvider } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { RedoPlatformRpcClientProvider } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { RedoProceduresRpcClientProvider } from "@redotech/redo-merchant-app-common/procedures/redo-procedures-rpc-client-provider";
import { RedoConversionRpcClientProvider } from "@redotech/redo-merchant-app-common/redo-conversion-rpc-client-provider";
import { RedoMerchantRpcClientProvider } from "@redotech/redo-merchant-app-common/rpc-client";
import { setSessionStartKey } from "@redotech/redo-merchant-app-common/session/session-expiration";
import { SessionExpirationModalProvider } from "@redotech/redo-merchant-app-common/session/session-expiration-modal";
import { RedoSupportRpcClientProvider } from "@redotech/redo-merchant-app-common/support/redo-support-rpc-client-provider";
import { PrintQueueProvider } from "@redotech/redo-merchant-fulfillment-app/common/print-queue-context";
import { PrinterProvider } from "@redotech/redo-merchant-fulfillment-app/common/printer-context";
import { OrderNavigationProvider } from "@redotech/redo-merchant-fulfillment-app/fulfillment-groups/order-navigation-context";
import { RedoOutboundLabelsRpcClientProvider } from "@redotech/redo-merchant-fulfillment-app/outbound-labels-rpc-client";
import { RedoAuthRpcClientProvider } from "@redotech/redo-merchant-login-app/auth-provider";
import { RedoManagedReturnsRpcClientProvider } from "@redotech/redo-merchant-managed-returns-app/rpc-client";
import { RedoReturnsProcessingRpcClientProvider } from "@redotech/redo-merchant-returns-processing-app/rpc-client";
import { RedoReviewsRpcClientProvider } from "@redotech/redo-merchant-review-app/rpc-client";
import { DevToolsOverlay } from "@redotech/redo-web-dev-tools/dev-tools-overlay";
import { AiSlidePanelProvider } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import { RedoClientContext } from "@redotech/redo-web/client";
import { MUI_THEME } from "@redotech/redo-web/mui-theme";
import { NotificationProvider } from "@redotech/redo-web/notification-provider";
import { reactQueryClient } from "@redotech/redo-web/rpc-query/react-query-config";
import { RpcQueryProvider } from "@redotech/redo-web/rpc-query/rpc-query-provider";
import { CustomSnackbarProvider } from "@redotech/redo-web/snackbar";
import { TextHighlighterProvider } from "@redotech/redo-web/text-highlighter-provider";
import { ThemeProvider as RedoThemeProvider } from "@redotech/redo-web/theme-provider";
import { PhoneWidgetProvider } from "@redotech/redo-web/voice/shared/phone-widget-context";
import { initMockSystem } from "@redotech/rpc-mock/init-mock";
import { StripeProvider } from "@redotech/stripe-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";
import { TanStackQueryDevtools } from "./app/tanstack-query-devtools";
import { TeamProvider } from "./app/team";
import { registerMerchantMockRestEndpoints } from "./mock-rest-endpoints";
import { routes } from "./route";
import "./styles.css";

initI18n();
setSessionStartKey();

if (
  process.env.NODE_ENV !== "development" &&
  process.env.REDO_MOCK_MODE !== "true"
) {
  if (DATADOG_CLIENT_TOKEN && DATADOG_APPLICATION_ID) {
    datadogRum.init({
      applicationId: DATADOG_APPLICATION_ID,
      clientToken: DATADOG_CLIENT_TOKEN,
      site: "datadoghq.com",
      service: "merchant-app",
      env: process.env.NODE_ENV || "production",
      version: REDO_MERCHANT_APP_VERSION,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 1,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      plugins: [reactPlugin({ router: true })],
      allowedTracingUrls: [/^https:\/\/[^/]+\.getredo\.com(?:\/|$)/],
    });
    datadogLogs.init({
      clientToken: DATADOG_CLIENT_TOKEN,
      site: "datadoghq.com",
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      service: "merchant-app",
      version: REDO_MERCHANT_APP_VERSION,
    });
  }
  amplitude.init(process.env.AMPLITUDE_API_KEY!, { defaultTracking: true });
}

const router = createBrowserRouter(routes, {
  ...(process.env.REDO_MOCK_MODE === "true" && {
    basename: window.location.pathname,
  }),
});

const actionPanelComponentContext: ActionPanelComponentContext = {
  ActivityPanel: lazyComponent(
    async () =>
      (
        await import(
          "@redotech/redo-merchant-customer-app/action-panel/activity-panel"
        )
      ).ActivityPanel,
  ),
  DuplicateOrderModal: lazyComponent(
    async () =>
      (await import("./order/action-panel/duplicate-order-modal"))
        .DuplicateOrderModal,
  ),
  OrderDetails: lazyComponent(
    async () =>
      (await import("./order/action-panel/order-details")).OrderDetails,
  ),
  MerchantCreateReturnModal: lazyComponent(
    async () =>
      (await import("./return/claim-return-shared")).MerchantCreateReturnModal,
  ),
  ReturnDetails: lazyComponent(
    async () =>
      (await import("./return/action-panel/return-details")).ReturnDetails,
  ),
};

const ErrorFallback = ({
  resetError,
  error,
}: {
  resetError: () => void;
  error: Error;
}) => (
  <div>
    <h2>Something went wrong</h2>
    <p>{String(error)}</p>
    <button onClick={resetError}>Try again</button>
  </div>
);

const MerchantApp = () => {
  return composeWrappers(
    (inner) => <ErrorBoundary fallback={ErrorFallback}>{inner}</ErrorBoundary>,
    (inner) => (
      <QueryClientProvider client={reactQueryClient}>
        {inner}
        <TanStackQueryDevtools />
      </QueryClientProvider>
    ),
    (inner) => <HidProvider>{inner}</HidProvider>,
    (inner) => <ThemeProvider theme={MUI_THEME}>{inner}</ThemeProvider>,
    (inner) => <RpcQueryProvider>{inner}</RpcQueryProvider>,
    (inner) => (
      <StripeProvider publicKey={process.env.STRIPE_PUBLIC_KEY!}>
        {inner}
      </StripeProvider>
    ),
    (inner) => (
      <IntercomProvider
        appId={process.env.INTERCOM_APP_ID || ""}
        autoBoot
        autoBootProps={{ hideDefaultLauncher: true }}
        shouldInitialize={false}
      >
        {inner}
      </IntercomProvider>
    ),
    (inner) => <TextHighlighterProvider>{inner}</TextHighlighterProvider>,
    (inner) => <NotificationProvider>{inner}</NotificationProvider>,
    (inner) => <CustomSnackbarProvider>{inner}</CustomSnackbarProvider>,
    (inner) => (
      <ActionPanelComponentContext.Provider value={actionPanelComponentContext}>
        {inner}
      </ActionPanelComponentContext.Provider>
    ),
    (inner) => <AuthProvider>{inner}</AuthProvider>,
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoClientContext.Provider
            value={new RedoClient(REDO_API_URL, auth?.token)}
          >
            {inner}
          </RedoClientContext.Provider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoAuthRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoAuthRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoMerchantClientProvider
            token={auth?.token}
            url={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoMerchantClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoConciergeRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoConciergeRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoConversionRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoConversionRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoCustomerRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoCustomerRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoMarketingRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoMarketingRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoIntegrationsRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoIntegrationsRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoInventoryRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoInventoryRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoManagedReturnsRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoManagedReturnsRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoLiquidationRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoLiquidationRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoOutboundLabelsRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoOutboundLabelsRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoReturnsProcessingRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoReturnsProcessingRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoReviewsRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoReviewsRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoMerchantRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoMerchantRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoPlatformRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoPlatformRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoProceduresRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoProceduresRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoSupportRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_MERCHANT_SERVER_URL}
          >
            {inner}
          </RedoSupportRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
    (inner) => <CurrencyExchangeProvider>{inner}</CurrencyExchangeProvider>,
    (inner) => <TeamProvider>{inner}</TeamProvider>,
    (inner) => <PrinterProvider>{inner}</PrinterProvider>,
    (inner) => <PrintQueueProvider>{inner}</PrintQueueProvider>,
    (inner) => (
      <MerchantAppEventServerProvider>{inner}</MerchantAppEventServerProvider>
    ),
    (inner) => (
      <SessionExpirationModalProvider>{inner}</SessionExpirationModalProvider>
    ),
    (inner) => <RedoThemeProvider>{inner}</RedoThemeProvider>,
    (inner) => <AlertContextProvider>{inner}</AlertContextProvider>,
    (inner) => <PhoneWidgetProvider>{inner}</PhoneWidgetProvider>,
    (inner) => <AiSlidePanelProvider>{inner}</AiSlidePanelProvider>,
    (inner) => <GlobalAgentProvider>{inner}</GlobalAgentProvider>,
    (inner) => <OrderNavigationProvider>{inner}</OrderNavigationProvider>,
    (inner) => (
      <>
        {inner}
        {(process.env.NODE_ENV === "development" ||
          process.env.REDO_MOCK_MODE === "true") && <DevToolsOverlay />}
      </>
    ),
  )(<RouterProvider router={router} />);
};

const root = createRoot(document.getElementById("root")!);

if (
  process.env.NODE_ENV === "development" ||
  process.env.REDO_MOCK_MODE === "true"
) {
  registerMerchantMockRestEndpoints();
  void initMockSystem()
    .catch((error) => {
      console.error("Mock system initialization failed:", error);
    })
    .then(() => root.render(<MerchantApp />));
} else {
  root.render(<MerchantApp />);
}
